import {engineContext} from './context.js';

(function(global){'use strict';
const VERSION='0.1.0-alpha';

function uniq(xs){ return [...new Set((xs||[]).filter(Boolean))]; }

function source(x){
  x=x||{};
  return Object.freeze({
    sourceId:x.sourceId||x.id||null,
    familyId:x.familyId||null,
    documentId:x.documentId||null,
    originId:x.originId||null,
    publisher:x.publisher||null,
    verification:x.verification||'UNVERIFIED',
    freshness:x.freshness||'UNKNOWN',
    extractedAt:x.extractedAt||null,
    claimIds:uniq(x.claimIds)
  });
}

function claim(x){
  x=x||{};
  return Object.freeze({
    claimId:x.claimId||x.id||null,
    text:x.text||null,
    sourceIds:uniq(x.sourceIds),
    conclusionIds:uniq(x.conclusionIds),
    verification:x.verification||'UNVERIFIED',
    status:x.status||'ACTIVE'
  });
}

function conclusion(x){
  x=x||{};
  return Object.freeze({
    conclusionId:x.conclusionId||x.id||null,
    claimIds:uniq(x.claimIds),
    evidenceIds:uniq(x.evidenceIds),
    status:x.status||'SUPPORTED'
  });
}

function lineage(input){
  input=input||{};
  const sources=(input.sources||[]).map(source);
  const claims=(input.claims||[]).map(claim);
  const conclusions=(input.conclusions||[]).map(conclusion);

  const sourceById=new Map(sources.map(x=>[x.sourceId,x]));
  const claimById=new Map(claims.map(x=>[x.claimId,x]));
  const conclusionById=new Map(conclusions.map(x=>[x.conclusionId,x]));

  return Object.freeze({
    version:VERSION,
    sources:Object.freeze(sources),
    claims:Object.freeze(claims),
    conclusions:Object.freeze(conclusions),
    sourceById,
    claimById,
    conclusionById
  });
}

function traceConclusion(graph,conclusionId){
  const g=graph&&graph.conclusionById?graph:lineage(graph||{});
  const c=g.conclusionById.get(conclusionId);
  if(!c) return Object.freeze({conclusionId,found:false,claims:[],sources:[],families:[],documents:[]});

  const claims=c.claimIds.map(id=>g.claimById.get(id)).filter(Boolean);
  const srcIds=uniq(claims.flatMap(x=>x.sourceIds).concat(c.evidenceIds));
  const sources=srcIds.map(id=>g.sourceById.get(id)).filter(Boolean);

  return Object.freeze({
    conclusionId,
    found:true,
    claims:Object.freeze(claims),
    sources:Object.freeze(sources),
    families:Object.freeze(uniq(sources.map(x=>x.familyId))),
    documents:Object.freeze(uniq(sources.map(x=>x.documentId))),
    origins:Object.freeze(uniq(sources.map(x=>x.originId)))
  });
}

function beliefReason(graph,conclusionId){
  const t=traceConclusion(graph,conclusionId);
  if(!t.found) return Object.freeze({supported:false,reason:'CONCLUSION_NOT_FOUND',verifiedSources:0,independentFamilies:0,claims:0});

  const verified=t.sources.filter(s=>s.verification==='VERIFIED');
  const families=uniq(verified.map(s=>s.familyId||s.originId||s.sourceId));
  const contradicted=t.claims.some(c=>c.status==='CONTRADICTED');
  const stale=verified.length>0 && verified.every(s=>s.freshness==='STALE');

  let reason='INSUFFICIENT_VERIFIED_LINEAGE';
  if(contradicted) reason='CONFLICTING_LINEAGE';
  else if(stale) reason='STALE_VERIFIED_LINEAGE';
  else if(verified.length && families.length>=2) reason='INDEPENDENT_VERIFIED_LINEAGE';
  else if(verified.length) reason='SINGLE_VERIFIED_LINEAGE';

  return Object.freeze({
    supported:verified.length>0 && !contradicted,
    reason,
    verifiedSources:verified.length,
    independentFamilies:families.length,
    claims:t.claims.length
  });
}

function orphanClaims(graph){
  const g=graph&&graph.claimById?graph:lineage(graph||{});
  return Object.freeze(g.claims.filter(c=>!c.sourceIds.some(id=>g.sourceById.has(id))));
}

function orphanConclusions(graph){
  const g=graph&&graph.conclusionById?graph:lineage(graph||{});
  return Object.freeze(g.conclusions.filter(c=>!c.claimIds.some(id=>g.claimById.has(id)) && !c.evidenceIds.some(id=>g.sourceById.has(id))));
}

function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});
  const g=lineage({
    sources:[
      {sourceId:'s1',familyId:'f1',documentId:'d1',originId:'o1',verification:'VERIFIED',freshness:'FRESH'},
      {sourceId:'s2',familyId:'f2',documentId:'d2',originId:'o2',verification:'VERIFIED',freshness:'FRESH'},
      {sourceId:'s3',familyId:'f1',documentId:'d3',originId:'o1',verification:'UNVERIFIED',freshness:'FRESH'}
    ],
    claims:[
      {claimId:'c1',sourceIds:['s1','s3'],conclusionIds:['k1'],verification:'VERIFIED',status:'ACTIVE'},
      {claimId:'c2',sourceIds:['s2'],conclusionIds:['k1'],verification:'VERIFIED',status:'ACTIVE'}
    ],
    conclusions:[
      {conclusionId:'k1',claimIds:['c1','c2'],status:'SUPPORTED'}
    ]
  });

  const tr=traceConclusion(g,'k1');
  ok('conclusion found',tr.found===true);
  ok('two claims traced',tr.claims.length===2);
  ok('three source links traced',tr.sources.length===3);
  ok('independent source families preserved',tr.families.length===2);
  ok('documents preserved',tr.documents.length===3);
  ok('origins preserved without collapsing family',tr.origins.length===2);

  const br=beliefReason(g,'k1');
  ok('verified lineage supports conclusion',br.supported===true);
  ok('independent corroboration recognized',br.reason==='INDEPENDENT_VERIFIED_LINEAGE');
  ok('verified source count correct',br.verifiedSources===2);
  ok('independent family count correct',br.independentFamilies===2);

  const g2=lineage({
    sources:[{sourceId:'s1',verification:'VERIFIED',freshness:'STALE'}],
    claims:[{claimId:'c1',sourceIds:['s1'],status:'ACTIVE'}],
    conclusions:[{conclusionId:'k1',claimIds:['c1']}]
  });
  ok('stale verified lineage is explicit',beliefReason(g2,'k1').reason==='STALE_VERIFIED_LINEAGE');

  const g3=lineage({
    sources:[{sourceId:'s1',verification:'VERIFIED',freshness:'FRESH'}],
    claims:[{claimId:'c1',sourceIds:['s1'],status:'CONTRADICTED'}],
    conclusions:[{conclusionId:'k1',claimIds:['c1']}]
  });
  ok('contradicted claim creates conflicting lineage',beliefReason(g3,'k1').reason==='CONFLICTING_LINEAGE');
  ok('conflicting lineage not marked supported',beliefReason(g3,'k1').supported===false);

  const g4=lineage({
    sources:[],
    claims:[{claimId:'orphan',sourceIds:['missing']}],
    conclusions:[{conclusionId:'orphanC',claimIds:['missingClaim']}]
  });
  ok('orphan claim detected',orphanClaims(g4).length===1);
  ok('orphan conclusion detected',orphanConclusions(g4).length===1);

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

const API={VERSION,source,claim,conclusion,lineage,traceConclusion,beliefReason,orphanClaims,orphanConclusions,selfTest};
try{API.selfTestResult=selfTest()}catch(e){API.bootstrapError=String(e&&e.stack||e)}
global.ZooInvestEvidenceLineageEngine=Object.freeze(API);
})(engineContext);